import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AlertService } from 'app/services/alert.service';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { SquareCategoryType } from '../../types';
import { AdapterService } from '../services/adapter.service';
import { SquareCategoryService } from '../services/square-category.service';
import * as SquareCategoryActions from './square-category.actions';

@Injectable()
export class SquareCategoryEffects {
    private _actions$ = inject(Actions);
    private _squareCategoryService = inject(SquareCategoryService);
    private _alertService = inject(AlertService);
    private _adapterService = inject(AdapterService);

    messages = {
        unknownError: 'Unknown error',
    };

    // -----------------------------------------------------------
    // @ Effects
    // -----------------------------------------------------------

    // Get All
    getAllSquareCategories$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SquareCategoryActions.loadSquareCategories),
            switchMap((action) =>
                this._squareCategoryService.getAll(action.params).pipe(
                    map((res) => {
                        const adaptedCategories = this._adapterService.adaptAll(
                            res.data
                        );
                        return SquareCategoryActions.loadSquareCategoriesSuccess(
                            {
                                squareCategories: adaptedCategories,
                            }
                        );
                    }),
                    catchError((error) =>
                        of(
                            SquareCategoryActions.loadSquareCategoriesFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                )
            )
        )
    );

    // Get One
    getSquareCategory$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SquareCategoryActions.loadSquareCategory),
            switchMap((action) =>
                this._squareCategoryService.getOne(action.id).pipe(
                    map((res) => {
                        const adaptedCategory =
                            this._adapterService.adaptSingle(
                                res.data,
                                SquareCategoryType.CATEGORY
                            );
                        return SquareCategoryActions.loadSquareCategorySuccess({
                            squareCategory: adaptedCategory,
                        });
                    }),
                    catchError((error) =>
                        of(
                            SquareCategoryActions.loadSquareCategoryFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                )
            )
        )
    );

    // Create
    createSquareCategory$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SquareCategoryActions.createSquareCategory),
            switchMap((action) =>
                this._squareCategoryService.create(action.payload).pipe(
                    map((res) => {
                        const adaptedCategory =
                            this._adapterService.adaptSingle(
                                res.data,
                                SquareCategoryType.CATEGORY
                            );
                        return SquareCategoryActions.createSquareCategorySuccess(
                            {
                                data: adaptedCategory,
                                success: 'Square category created successfully',
                                parentSquareId: action.payload
                                    .parent_category_id as unknown as
                                    | string
                                    | undefined,
                            }
                        );
                    }),
                    catchError((error) =>
                        of(
                            SquareCategoryActions.createSquareCategoryFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                )
            )
        )
    );

    // Update
    updateSquareCategory$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SquareCategoryActions.updateSquareCategory),
            switchMap((action) =>
                this._squareCategoryService
                    .update(
                        action.payload,
                        action.squareId,
                        action.categoryType
                    )
                    .pipe(
                        map((res) =>
                            SquareCategoryActions.updateSquareCategorySuccess({
                                data: res.data,
                                success: 'Square category updated successfully',
                            })
                        ),
                        catchError((error) =>
                            of(
                                SquareCategoryActions.updateSquareCategoryFailure(
                                    {
                                        error:
                                            error?.error?.message ||
                                            this.messages.unknownError,
                                    }
                                )
                            )
                        )
                    )
            )
        )
    );

    // Delete
    deleteSquareCategory$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SquareCategoryActions.deleteSquareCategory),
            switchMap((action) =>
                this._squareCategoryService
                    .delete(action.squareId, action.categoryType, action.params)
                    .pipe(
                        map((res) => {
                            const squareId = res.data.square_id;
                            return SquareCategoryActions.deleteSquareCategorySuccess(
                                {
                                    squareId,
                                    success:
                                        'Square category deleted successfully',
                                }
                            );
                        }),
                        catchError((error) =>
                            of(
                                SquareCategoryActions.deleteSquareCategoryFailure(
                                    {
                                        error:
                                            error?.error?.message ||
                                            this.messages.unknownError,
                                    }
                                )
                            )
                        )
                    )
            )
        )
    );

    // -----------------------------------------------------------
    // @ Side Effects (alerts)
    // -----------------------------------------------------------

    onSquareCategoryActionSuccess$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    SquareCategoryActions.createSquareCategorySuccess,
                    SquareCategoryActions.updateSquareCategorySuccess,
                    SquareCategoryActions.deleteSquareCategorySuccess
                ),
                tap((action) => {
                    this._alertService.showAlert(action.success, 'success');
                })
            ),
        { dispatch: false }
    );

    onSquareCategoryActionFailure$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    SquareCategoryActions.loadSquareCategoriesFailure,
                    SquareCategoryActions.loadSquareCategoryFailure,
                    SquareCategoryActions.createSquareCategoryFailure,
                    SquareCategoryActions.updateSquareCategoryFailure,
                    SquareCategoryActions.deleteSquareCategoryFailure
                ),
                tap((action) => {
                    this._alertService.showAlert(action.error, 'error');
                })
            ),
        { dispatch: false }
    );
}
